/**
 * Impersonate Store
 *
 * Global state for impersonate (access as another user) functionality
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ImpersonateInfo, ImpersonateUserInfo } from '@/services/impersonateApi';

interface ImpersonateState {
  // State
  isImpersonating: boolean;
  originalUser: ImpersonateUserInfo | null;
  impersonatedUser: ImpersonateUserInfo | null;
  startedAt: string | null;

  // Actions
  setImpersonating: (data: {
    originalUser: ImpersonateUserInfo;
    impersonatedUser: ImpersonateUserInfo;
  }) => void;
  clearImpersonate: () => void;
  syncFromApi: (info: ImpersonateInfo) => void;
}

export const useImpersonateStore = create<ImpersonateState>()(
  persist(
    (set) => ({
      // Initial State
      isImpersonating: false,
      originalUser: null,
      impersonatedUser: null,
      startedAt: null,

      // Set impersonating state
      setImpersonating: ({ originalUser, impersonatedUser }) =>
        set({
          isImpersonating: true,
          originalUser,
          impersonatedUser,
          startedAt: new Date().toISOString(),
        }),

      // Clear impersonate state
      clearImpersonate: () =>
        set({
          isImpersonating: false,
          originalUser: null,
          impersonatedUser: null,
          startedAt: null,
        }),

      // Sync state from API response
      syncFromApi: (info: ImpersonateInfo) => {
        if (info.isImpersonating) {
          set({
            isImpersonating: true,
            originalUser: {
              id: info.originalUserId!,
              login: info.originalUsername!,
              name: info.originalName!,
              roles: [],
              primaryRole: null,
            },
            impersonatedUser: {
              id: info.impersonatedUserId!,
              login: info.impersonatedUsername!,
              name: info.impersonatedName!,
              roles: [],
              primaryRole: info.impersonatedPrimaryRole ?? null,
            },
            startedAt: info.startedAt || null,
          });
        } else {
          set({
            isImpersonating: false,
            originalUser: null,
            impersonatedUser: null,
            startedAt: null,
          });
        }
      },
    }),
    {
      name: 'tmf-impersonate-storage',
      partialize: (state) => ({
        isImpersonating: state.isImpersonating,
        originalUser: state.originalUser,
        impersonatedUser: state.impersonatedUser,
        startedAt: state.startedAt,
      }),
    }
  )
);

// Selectors
export const selectIsImpersonating = (state: ImpersonateState) => state.isImpersonating;
export const selectOriginalUser = (state: ImpersonateState) => state.originalUser;
export const selectImpersonatedUser = (state: ImpersonateState) => state.impersonatedUser;

// Convenience getters
export const getImpersonateState = () => useImpersonateStore.getState();
export const isCurrentlyImpersonating = () => useImpersonateStore.getState().isImpersonating;
